import {
  ArrowLeftRight, BadgePercent, Banknote, Bot, Briefcase, Boxes, CalendarClock, ClipboardCheck, Coins, CreditCard,
  Factory, FilePlus2, FileSignature, Film, FolderOpen, FolderTree, Gift, Globe, Heart, type LucideIcon, Images, Layers,
  FileChartColumn, Lock, MessageCircle, Monitor, Navigation, Package, PackagePlus, Percent, Receipt, Search, Settings2, Shapes, ShieldCheck,
  ShoppingCart, Smartphone, Sparkles, SlidersHorizontal, Star, Store, Ticket, Truck, Undo2, UsersRound, Wallet,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Input, Skeleton } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { TOGGLEABLE_MODULES, type ModuleKey } from '../../lib/modules.js';
import { useModulesStore } from '../../lib/modulesStore.js';

type Flags = Record<ModuleKey, boolean>;

/** Exhaustive on purpose — a new module fails the build until it gets an icon. */
const MODULE_ICON: Record<ModuleKey, LucideIcon> = {
  files: FolderOpen,
  crm: UsersRound,
  hr: Briefcase,
  installments: CalendarClock,
  expenses: Receipt,
  vouchers: Banknote,
  pos: CreditCard,
  coupons: Ticket,
  discounts: BadgePercent,
  cashback: Gift,
  mandob: Navigation,
  quotations: FileSignature,
  cms: Store,
  audit: ShieldCheck,
  pricing: Coins,
  ai: Bot,
  imagegen: Images,
  customer_display: Monitor,
  returns: Undo2,
  fib: Smartphone,
  otp: MessageCircle,
  reviews: Star,
  wishlist: Heart,
  stocktake: ClipboardCheck,
  treasury: ArrowLeftRight,
  batches: Layers,
  variants: Shapes,
  storefront_ai: Sparkles,
  delivery: Truck,
  storefront_whatsapp: MessageCircle,
  tax: Percent,
  tax_iq: Percent,
  tax_de: Percent,
  stories: Film,
  manual_sales: FilePlus2,
  manager_products: PackagePlus,
  multi_category: FolderTree,
  custom_fields: SlidersHorizontal,
  manufacturing: Factory,
  combos: Package,
  ai_reports: FileChartColumn,
};

/**
 * Which part of the business a module belongs to.
 *
 * Thirty-seven switches in one undifferentiated grid is a search problem, not a
 * settings page: the shop owner looking for "returns" had to read every card.
 * Grouped, the same list becomes five short lists you can skim by heading.
 * Exhaustive like the icon map — a new module cannot quietly land in nowhere.
 */
type GroupKey = 'selling' | 'inventory' | 'online' | 'money' | 'workspace';

const GROUP_ORDER: GroupKey[] = ['selling', 'inventory', 'online', 'money', 'workspace'];

const GROUP_ICON: Record<GroupKey, LucideIcon> = {
  selling: ShoppingCart,
  inventory: Boxes,
  online: Globe,
  money: Wallet,
  workspace: Settings2,
};

const MODULE_GROUP: Record<ModuleKey, GroupKey> = {
  // Selling — the counter, the road, and everything that prices a sale.
  pos: 'selling',
  customer_display: 'selling',
  manual_sales: 'selling',
  quotations: 'selling',
  returns: 'selling',
  pricing: 'selling',
  discounts: 'selling',
  coupons: 'selling',
  cashback: 'selling',
  combos: 'selling',
  installments: 'selling',
  mandob: 'selling',
  delivery: 'selling',
  // Inventory — what is on the shelf and how it is described.
  stocktake: 'inventory',
  batches: 'inventory',
  variants: 'inventory',
  multi_category: 'inventory',
  custom_fields: 'inventory',
  manufacturing: 'inventory',
  // Online — the public shop, the app, and what lives on them.
  cms: 'online',
  stories: 'online',
  reviews: 'online',
  wishlist: 'online',
  storefront_ai: 'online',
  storefront_whatsapp: 'online',
  fib: 'online',
  manager_products: 'online',
  // Money — books, cash and tax.
  expenses: 'money',
  vouchers: 'money',
  treasury: 'money',
  tax: 'money',
  tax_iq: 'money',
  tax_de: 'money',
  ai_reports: 'money',
  // Workspace — the tools around the business rather than the business itself.
  files: 'workspace',
  crm: 'workspace',
  hr: 'workspace',
  audit: 'workspace',
  ai: 'workspace',
  imagegen: 'workspace',
  otp: 'workspace',
};

/** Position within a group — MODULE_GROUP is declared in reading order. */
const GROUP_RANK = new Map<string, number>(Object.keys(MODULE_GROUP).map((k, i) => [k, i]));

type Filter = 'all' | 'on' | 'off';

function allOn(): Flags {
  const out = {} as Flags;
  for (const m of TOGGLEABLE_MODULES) out[m] = true;
  return out;
}
function allOff(): Flags {
  const out = {} as Flags;
  for (const m of TOGGLEABLE_MODULES) out[m] = false;
  return out;
}

/**
 * Settings → Modules. Turn the optional modules on/off for the tenant. Saved as
 * the `modules` setting. Modules the platform has LOCKED (`modules_locked`) are
 * shown disabled-and-off — the tenant can't re-enable them here. The nav + route
 * guards read the effective state via the modules store, so toggles take effect
 * immediately.
 *
 * Each module is one switch-card (the whole card is the hit target), the cards
 * are grouped by part of the business, and a search box plus On/Off filters cut
 * the list down. Save only appears once something actually changed.
 */
export function ModulesPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('settings.write');
  const tenantId = useAuthStore((s) => s.tenant?.id ?? null);
  const [loaded, setLoaded] = useState(false);
  const [flags, setFlags] = useState<Flags>(allOn);
  /** The last saved state — drives the "unsaved changes" affordance. */
  const [baseline, setBaseline] = useState<Flags>(allOn);
  const [locked, setLocked] = useState<Flags>(allOff);
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: { key: string; value: unknown }[] }>('/settings');
    const mv = r.data.items.find((s) => s.key === 'modules')?.value as Record<string, unknown> | undefined;
    const lv = r.data.items.find((s) => s.key === 'modules_locked')?.value as Record<string, unknown> | undefined;
    const f = {} as Flags;
    const l = {} as Flags;
    for (const m of TOGGLEABLE_MODULES) {
      f[m] = mv?.[m] !== false;
      l[m] = lv?.[m] === true;
    }
    setFlags(f);
    setBaseline(f);
    setLocked(l);
    setLoaded(true);
  };

  useEffect(() => {
    void reload();
  }, []);

  const label = (m: ModuleKey): string => t(`modules.items.${m}.label`, { defaultValue: m });
  const hint = (m: ModuleKey): string => t(`modules.items.${m}.hint`, { defaultValue: '' });
  const isOn = (m: ModuleKey): boolean => flags[m] && !locked[m];

  /** Search + On/Off filter, then bucketed by group in the fixed group order. */
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = TOGGLEABLE_MODULES.filter((m) => {
      if (filter === 'on' && !(flags[m] && !locked[m])) return false;
      if (filter === 'off' && (flags[m] && !locked[m])) return false;
      if (!q) return true;
      return label(m).toLowerCase().includes(q) || hint(m).toLowerCase().includes(q) || m.includes(q);
    });
    return GROUP_ORDER.map((g) => ({
      key: g,
      // The group's total is its FULL membership, not the filtered slice — "3 of
      // 8 on" has to keep meaning the same thing while you search.
      total: TOGGLEABLE_MODULES.filter((m) => MODULE_GROUP[m] === g).length,
      on: TOGGLEABLE_MODULES.filter((m) => MODULE_GROUP[m] === g && flags[m] && !locked[m]).length,
      modules: matches
        .filter((m) => MODULE_GROUP[m] === g)
        .sort((a, b) => (GROUP_RANK.get(a) ?? 0) - (GROUP_RANK.get(b) ?? 0)),
    })).filter((g) => g.modules.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, filter, flags, locked, t]);

  const enabledCount = TOGGLEABLE_MODULES.filter((m) => flags[m] && !locked[m]).length;
  const total = TOGGLEABLE_MODULES.length;
  const pct = Math.round((enabledCount / total) * 100);
  const offCount = total - enabledCount;
  const dirty = TOGGLEABLE_MODULES.some((m) => flags[m] !== baseline[m]);
  const anyVisible = groups.some((g) => g.modules.length > 0);

  const toggle = (m: ModuleKey): void => {
    if (!canWrite || locked[m]) return;
    setSavedNote(null);
    setFlags((f) => ({ ...f, [m]: !f[m] }));
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSavedNote(null);
    try {
      await api().put('/settings/modules', { value: flags });
      if (tenantId) {
        const effective = {} as Flags;
        for (const m of TOGGLEABLE_MODULES) effective[m] = flags[m] && !locked[m];
        useModulesStore.getState().setModules(tenantId, effective);
      }
      setBaseline(flags);
      setSavedNote(t('modules.saved', { defaultValue: 'Modules updated.' }));
    } catch {
      setError(t('modules.error', { defaultValue: 'Could not save the modules.' }));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 9 }, (_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      </Card>
    );
  }

  const chip = (value: Filter, text: string, count: number): JSX.Element => {
    const active = filter === value;
    return (
      <button
        type="button"
        onClick={() => setFilter(value)}
        aria-pressed={active}
        className={[
          'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-150',
          active
            ? 'bg-brand text-brand-fg'
            : 'border border-border bg-bg-subtle text-fg-muted hover:text-fg',
        ].join(' ')}
      >
        {text}
        <span className={['ms-1.5 tabular-nums', active ? 'opacity-80' : 'opacity-60'].join(' ')}>{count}</span>
      </button>
    );
  };

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      <Card className="overflow-hidden p-0">
        <div className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold">{t('modules.title', { defaultValue: 'Modules' })}</h2>
              <p className="mt-1 max-w-2xl text-sm text-fg-muted">
                {t('modules.description', {
                  defaultValue:
                    'Turn optional modules on or off. Disabled modules are hidden from the menu; their data is kept and returns when you re-enable.',
                })}
              </p>
            </div>
            {/* The count reads as a headline number, not a caption — it is the
                one fact this page exists to state. */}
            <div className="shrink-0 text-end">
              <div className="text-2xl font-bold tabular-nums leading-none">
                {enabledCount}
                <span className="text-base font-medium text-fg-muted">/{total}</span>
              </div>
              <div className="mt-1 text-xs text-fg-muted">
                {t('modules.enabledLabel', { defaultValue: 'modules on' })}
              </div>
            </div>
          </div>

          <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-bg-muted">
            <div className="h-full rounded-full bg-brand transition-[width] duration-500 ease-out" style={{ width: `${pct}%` }} />
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="relative min-w-[14rem] flex-1 sm:max-w-sm">
              <Search className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" aria-hidden />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('modules.search', { defaultValue: 'Search modules…' })}
                aria-label={t('modules.search', { defaultValue: 'Search modules…' })}
                className="ps-9"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {chip('all', t('modules.filterAll', { defaultValue: 'All' }), total)}
              {chip('on', t('modules.filterOn', { defaultValue: 'On' }), enabledCount)}
              {chip('off', t('modules.filterOff', { defaultValue: 'Off' }), offCount)}
            </div>
          </div>
        </div>
      </Card>

      {!anyVisible ? (
        <Card className="p-10 text-center text-sm text-fg-muted">
          {/* Say what is actually true: an empty "Off" list means everything is
              on, which is good news, not a failed search. */}
          {!query.trim() && filter === 'off'
            ? t('modules.noneOff', { defaultValue: 'Every module is switched on.' })
            : !query.trim() && filter === 'on'
              ? t('modules.noneOn', { defaultValue: 'Every module is switched off.' })
              : t('modules.noMatches', { defaultValue: 'No modules match your search.' })}
        </Card>
      ) : (
        groups.map((group) => {
          const GroupIcon = GROUP_ICON[group.key];
          return (
            <section key={group.key} className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <GroupIcon className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden />
                <h3 className="shrink-0 text-sm font-bold uppercase tracking-wide text-fg-muted">
                  {t(`modules.groups.${group.key}`, { defaultValue: group.key })}
                </h3>
                <span className="shrink-0 rounded-full border border-border bg-bg-subtle px-2 py-0.5 text-[11px] font-semibold tabular-nums text-fg-muted">
                  {group.on}/{group.total}
                </span>
                {/* A rule out to the edge: gives each block a spine without a heavy header bar. */}
                <span className="h-px min-w-0 flex-1 bg-border" aria-hidden />
              </div>

              {/* `h-full` on each card levels the cards WITHIN a row; leaving the
                  rows to size themselves keeps a short card from inheriting a
                  three-line neighbour's height from somewhere else on the page. */}
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {group.modules.map((m) => {
                  const Icon = MODULE_ICON[m];
                  const isLocked = locked[m];
                  const on = isOn(m);
                  const interactive = canWrite && !isLocked;
                  return (
                    <button
                      key={m}
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={label(m)}
                      disabled={!interactive}
                      onClick={() => toggle(m)}
                      className={[
                        'group flex h-full items-start gap-3 rounded-xl border p-4 text-start transition-[background-color,border-color,box-shadow,transform] duration-200 ease-out',
                        interactive ? 'cursor-pointer hover:shadow-md active:scale-[0.99]' : 'cursor-not-allowed',
                        on
                          ? 'border-[color-mix(in_oklab,var(--color-brand)_45%,transparent)] bg-[color-mix(in_oklab,var(--color-brand)_8%,transparent)] hover:border-brand'
                          : 'border-border bg-[color-mix(in_oklab,var(--color-bg-subtle)_50%,transparent)] hover:border-border-strong',
                        isLocked ? 'opacity-60' : '',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'grid h-10 w-10 shrink-0 place-items-center rounded-lg transition-colors duration-200',
                          on ? 'bg-[color-mix(in_oklab,var(--color-brand)_16%,transparent)] text-brand' : 'bg-bg-muted text-fg-subtle',
                        ].join(' ')}
                      >
                        {isLocked ? <Lock className="h-5 w-5" aria-hidden /> : <Icon className="h-5 w-5" aria-hidden />}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-fg">{label(m)}</span>
                        {/* No `block` here: line-clamp needs its own display value,
                            and `block` was quietly cancelling the clamp — which is
                            why long hints used to run to four lines and leave the
                            grid ragged. */}
                        <span className="mt-0.5 line-clamp-2 text-xs leading-snug text-fg-muted">
                          {isLocked
                            ? t('modules.lockedHint', { defaultValue: 'Disabled by your provider — contact them to enable it.' })
                            : hint(m)}
                        </span>
                      </span>

                      {/* Switch — visual only; the whole card is the control. */}
                      <span
                        aria-hidden
                        className={[
                          'relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors duration-200',
                          on ? 'bg-brand' : 'bg-border',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'absolute top-0.5 start-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ease-out',
                            on ? 'ltr:translate-x-4 rtl:-translate-x-4' : '',
                          ].join(' ')}
                        />
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })
      )}

      {error && <p className="text-sm text-danger" role="alert">{error}</p>}
      {savedNote && <p className="text-sm text-success" role="status">{savedNote}</p>}

      {canWrite && dirty && (
        <div className="sticky bottom-4 z-10">
          <Card className="flex flex-wrap items-center justify-between gap-3 p-3 shadow-lg">
            <span className="text-sm text-fg-muted">
              {t('modules.unsaved', { defaultValue: 'You have unsaved changes.' })}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" disabled={saving} onClick={() => setFlags(baseline)}>
                {tc('actions.cancel')}
              </Button>
              <Button type="submit" loading={saving}>{tc('actions.save')}</Button>
            </div>
          </Card>
        </div>
      )}
    </form>
  );
}
